// ================================================================
// CredentialsPanel — مدیریت کلیدهای API مدل‌های AI (async)
// ================================================================

import './credentials.css';
import { t } from '../../i18n/index.js';
import { icon, escapeHtml, delegate } from '../../utils/dom.js';
import {
  PROVIDERS, getCredentials, createCredential, deleteCredential
} from '../../core/credentials-store.js';

let overlay = null;
const s = {
  items: [],
  loading: false,
  addProvider: 'openai',
  addLabel: '',
  addKey: '',
  confirmDelete: null,
  error: ''
};

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`;
  } catch (e) {
    return iso;
  }
}

function providerLabel(key) {
  const pr = PROVIDERS.find(x => x.key === key);
  return pr ? t(pr.labelKey) : key;
}

export function openCredentialsPanel() {
  if (overlay) return;
  overlay = document.createElement('div');
  overlay.className = 'cred-overlay';
  document.body.appendChild(overlay);
  Object.assign(s, {
    items: [], loading: false, addProvider: 'openai',
    addLabel: '', addKey: '', confirmDelete: null, error: ''
  });
  draw();
  delegate(overlay, '[data-action]', 'click', onAction);
  delegate(overlay, '[data-field]', 'input', onInput);
  delegate(overlay, '[data-field]', 'change', onInput);
  load();
}

function close() {
  if (overlay) { overlay.remove(); overlay = null; }
}

async function load() {
  s.loading = true; draw();
  try {
    s.items = await getCredentials();
  } catch (e) {
    console.error('load credentials failed', e);
    s.items = [];
  }
  s.loading = false; draw();
}

function onInput(e, el) {
  const f = el.dataset.field;
  if (f === 'addProvider') s.addProvider = el.value;
  else if (f === 'addLabel') s.addLabel = el.value;
  else if (f === 'addKey') s.addKey = el.value;
}

function onAction(e, el) {
  const a = el.dataset.action;
  const id = el.dataset.id;
  switch (a) {
    case 'close': return close();
    case 'add': return doAdd();
    case 'ask-delete': { s.confirmDelete = id; return draw(); }
    case 'cancel-delete': { s.confirmDelete = null; return draw(); }
    case 'confirm-delete': return doDelete(id);
    case 'pick-provider': { s.addProvider = el.dataset.provider; return draw(); }
  }
}

async function doAdd() {
  s.error = '';
  if (!s.addLabel.trim() || !s.addKey.trim()) {
    s.error = t('credentials.errorEmpty');
    return draw();
  }
  try {
    await createCredential(s.addProvider, s.addLabel.trim(), s.addKey.trim());
    s.addLabel = '';
    s.addKey = '';
    await load();
  } catch (e) {
    console.error('add credential failed', e);
    s.error = t('credentials.errorSave');
    draw();
  }
}

async function doDelete(id) {
  try {
    await deleteCredential(id);
    s.confirmDelete = null;
    await load();
  } catch (e) { console.error('delete failed', e); }
}

function drawProviderSection(prov) {
  const items = s.items.filter(it => it.aiProvider === prov.key);
  const rows = items.length
    ? items.map(it => {
        const confirming = s.confirmDelete === it.id;
        return `
          <div class="cred-row">
            <div class="cred-row-info">
              ${icon('key', 16)}
              <span class="cred-label">${escapeHtml(it.label)}</span>
              <span class="cred-date">${escapeHtml(fmtDate(it.createdAt))}</span>
            </div>
            ${confirming
              ? `<span class="cred-confirm">
                   <button class="cred-confirm-yes" data-action="confirm-delete" data-id="${escapeHtml(it.id)}" title="${escapeHtml(t('credentials.confirmDelete'))}">${icon('check', 16)}</button>
                   <button class="cred-confirm-no" data-action="cancel-delete" title="${escapeHtml(t('credentials.cancel'))}">${icon('close', 16)}</button>
                 </span>`
              : `<button class="cred-del" data-action="ask-delete" data-id="${escapeHtml(it.id)}" title="${escapeHtml(t('credentials.delete'))}">${icon('delete', 16)}</button>`
            }
          </div>
        `;
      }).join('')
    : `<div class="cred-empty-prov">${escapeHtml(t('credentials.noKeys'))}</div>`;

  return `
    <div class="cred-prov-section">
      <div class="cred-prov-head">${escapeHtml(providerLabel(prov.key))}</div>
      ${rows}
    </div>
  `;
}

function drawAddForm() {
  const provOpts = PROVIDERS.map(pr => `
    <button class="cred-prov-pick ${pr.key === s.addProvider ? 'active' : ''}" data-action="pick-provider" data-provider="${pr.key}">
      ${escapeHtml(providerLabel(pr.key))}
    </button>
  `).join('');

  return `
    <div class="cred-add">
      <div class="cred-add-title">${icon('add_circle', 18)} ${escapeHtml(t('credentials.addTitle'))}</div>
      <div class="cred-add-provs">${provOpts}</div>
      <input type="text" class="cred-input" data-field="addLabel" placeholder="${escapeHtml(t('credentials.labelPlaceholder'))}" value="${escapeHtml(s.addLabel)}" />
      <input type="password" class="cred-input" data-field="addKey" placeholder="${escapeHtml(t('credentials.keyPlaceholder'))}" value="${escapeHtml(s.addKey)}" autocomplete="off" />
      ${s.error ? `<div class="cred-error">${escapeHtml(s.error)}</div>` : ''}
      <button class="cred-add-btn" data-action="add">${icon('save', 16)} ${escapeHtml(t('credentials.addBtn'))}</button>
    </div>
  `;
}

function draw() {
  if (!overlay) return;
  const sections = PROVIDERS.map(drawProviderSection).join('');
  overlay.innerHTML = `
    <div class="cred-panel">
      <div class="cred-header">
        <h2>${icon('shield', 20)} ${escapeHtml(t('credentials.title'))}</h2>
        <button class="cred-close" data-action="close">${icon('close', 20)}</button>
      </div>
      <div class="cred-body">
        <section class="cred-list">
          <div class="cred-hint">${icon('info', 14)} ${escapeHtml(t('credentials.hint'))}</div>
          ${sections}
        </section>
        <aside class="cred-side">
          ${drawAddForm()}
        </aside>
      </div>
    </div>
  `;
}
